/*
  AdminManualInvoiceDialog · iter100.205
  =======================================
  Reusable "Create invoice manually" dialog for admins.
  Used from:
    - Admin Billing page header (`Billing.jsx`) → doctor unselected, admin picks
    - Admin Doctor Profile → Billing tab (`DoctorProfile.jsx BillingTab`) → doctor pre-filled

  UX:
    - Doctor picker (searchable dropdown, required)
    - Optional case picker (loads doctor's cases when doctor selected)
    - One-click quick-add templates: Refinement / Rush / Course / Consultation / Refund adjustment
    - Add / remove line items rows (description × qty × unit price)
    - Live subtotal + tax + total
    - Tax % + due date + notes
    - POSTs to /api/admin/provider-invoices — backend now accepts optional case_id (iter100.205)
    - On success: fires onCreated(invoice) so parent can reload list

  Templates ship with sensible defaults. Refund adjustment starts negative (-1000) so it acts
  as a credit memo line without needing a separate endpoint.
*/
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Plus, Trash, ReceiptX, Sparkle, Lightning, GraduationCap,
    ChatCircleText, ArrowUUpLeft, CurrencyInr, MagnifyingGlass,
} from "@phosphor-icons/react";

/* Quick-add line-item templates. Refund is negative so it deducts. */
const LINE_TEMPLATES = [
    { key: "refinement",   label: "Refinement fee",   description: "Refinement fee — additional aligner batch",       unit_price: 5000,  Icon: Sparkle },
    { key: "rush",         label: "Rush charge",      description: "Rush production surcharge",                       unit_price: 3000,  Icon: Lightning },
    { key: "course",       label: "Course fee",       description: "Academy course fee",                              unit_price: 15000, Icon: GraduationCap },
    { key: "consultation", label: "Consultation",     description: "CoE consultation / planning support",             unit_price: 1500,  Icon: ChatCircleText },
    { key: "refund",       label: "Refund adjustment", description: "Refund / credit adjustment",                     unit_price: -1000, Icon: ArrowUUpLeft },
];

let _liSeq = 0;
const newLiKey = () => `mli-${++_liSeq}`;
const blankLi = () => ({ _key: newLiKey(), description: "", quantity: 1, unit_price: 0 });

const formatINR = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

/* Default due date = today + 30 days, formatted YYYY-MM-DD for <input type="date">. */
const defaultDueDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
};

export default function AdminManualInvoiceDialog({
    open,
    onOpenChange,
    onCreated,           // (invoice) => void — called after successful POST
    presetDoctorId,      // if set, doctor picker is disabled and locked to this ID
    presetDoctorName,    // display label when doctor is preset (avoids extra fetch)
}) {
    const [doctors, setDoctors] = useState([]);
    const [doctorId, setDoctorId] = useState(presetDoctorId || "");
    const [doctorSearch, setDoctorSearch] = useState("");
    const [cases, setCases] = useState([]);
    const [caseId, setCaseId] = useState("");
    const [lineItems, setLineItems] = useState([blankLi()]);
    const [taxPct, setTaxPct] = useState(0);
    const [dueAt, setDueAt] = useState(defaultDueDate());
    const [notes, setNotes] = useState("");
    const [currency, setCurrency] = useState("INR");
    const [busy, setBusy] = useState(false);
    const [doctorsLoading, setDoctorsLoading] = useState(false);
    const [casesLoading, setCasesLoading] = useState(false);

    /* Reset dialog state whenever it (re)opens. */
    useEffect(() => {
        if (!open) return;
        setDoctorId(presetDoctorId || "");
        setDoctorSearch("");
        setCases([]);
        setCaseId("");
        setLineItems([blankLi()]);
        setTaxPct(0);
        setDueAt(defaultDueDate());
        setNotes("");
        setCurrency("INR");
    }, [open, presetDoctorId]);

    /* Load doctor list (once, when dialog opens without a preset). */
    useEffect(() => {
        if (!open || presetDoctorId) return;
        setDoctorsLoading(true);
        api.get("/admin/doctors")
            .then((r) => setDoctors(r.data || []))
            .catch(() => setDoctors([]))
            .finally(() => setDoctorsLoading(false));
    }, [open, presetDoctorId]);

    /* Load cases when doctor changes. */
    useEffect(() => {
        if (!doctorId) { setCases([]); return; }
        setCasesLoading(true);
        api.get(`/admin/doctors/${doctorId}/cases`)
            .then((r) => setCases(r.data || []))
            .catch(() => setCases([]))
            .finally(() => setCasesLoading(false));
    }, [doctorId]);

    const filteredDoctors = useMemo(() => {
        const q = doctorSearch.trim().toLowerCase();
        if (!q) return doctors;
        return doctors.filter((d) =>
            (d.name || "").toLowerCase().includes(q) ||
            (d.clinic_name || "").toLowerCase().includes(q)
        );
    }, [doctors, doctorSearch]);

    const { subtotal, taxAmount, total } = useMemo(() => {
        const sub = lineItems.reduce((n, li) => n + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
        const tax = sub * (Number(taxPct) || 0) / 100;
        return { subtotal: sub, taxAmount: tax, total: sub + tax };
    }, [lineItems, taxPct]);

    const addTemplate = (t) => {
        setLineItems((prev) => {
            // Replace the first blank row if empty, else append.
            const isEmpty = prev.length === 1 && !prev[0].description && !prev[0].unit_price;
            const row = { _key: newLiKey(), description: t.description, quantity: 1, unit_price: t.unit_price };
            return isEmpty ? [row] : [...prev, row];
        });
    };
    const addBlank = () => setLineItems((prev) => [...prev, blankLi()]);
    const removeLi = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));
    const setLi = (idx, key, val) => {
        setLineItems((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [key]: val };
            return next;
        });
    };

    const canSubmit = doctorId && lineItems.some((li) => (li.description || "").trim() && (Number(li.quantity) || 0) > 0);

    const submit = async () => {
        if (!canSubmit) {
            toast.error("Doctor + at least one valid line item required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                doctor_id: doctorId,
                case_id: caseId || null,
                currency,
                due_at: dueAt ? new Date(dueAt + "T23:59:59Z").toISOString() : undefined,
                notes: notes || undefined,
                tax_pct: parseFloat(taxPct) || 0,
                line_items: lineItems
                    .filter((li) => (li.description || "").trim())
                    .map(({ _key, ...rest }) => ({
                        description: rest.description,
                        quantity: Number(rest.quantity) || 1,
                        unit_price: Number(rest.unit_price) || 0,
                    })),
            };
            const r = await api.post("/admin/provider-invoices", payload);
            toast.success(`Invoice ${r.data.code} created`);
            onCreated?.(r.data);
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create invoice");
        } finally {
            setBusy(false);
        }
    };

    const doctorLabel = presetDoctorId
        ? (presetDoctorName || "Selected doctor")
        : (doctors.find((d) => d.id === doctorId)?.name || "");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="manual-invoice-dialog">
                <DialogHeader>
                    <DialogTitle className="inline-flex items-center gap-2">
                        <ReceiptX size={20} weight="duotone" className="text-primary" />
                        Create invoice manually
                    </DialogTitle>
                    <DialogDescription>
                        Manual override for one-off charges (course fees, refunds, custom adjustments). Auto-generated
                        case invoices continue to work; use this only when needed.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* DOCTOR PICKER */}
                    <div>
                        <Label className="text-xs font-semibold">Doctor <span className="text-rose-600">*</span></Label>
                        {presetDoctorId ? (
                            <div className="mt-1 h-9 px-3 rounded-md border border-border bg-secondary/40 flex items-center text-sm font-medium" data-testid="mi-doctor-preset">
                                {doctorLabel}
                            </div>
                        ) : (
                            <>
                                <div className="mt-1 relative">
                                    <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={doctorSearch}
                                        onChange={(e) => setDoctorSearch(e.target.value)}
                                        placeholder={doctorsLoading ? "Loading doctors…" : "Search doctor by name or clinic"}
                                        className="h-9 pl-8 text-sm"
                                        data-testid="mi-doctor-search"
                                    />
                                </div>
                                <div className="mt-2 max-h-36 overflow-y-auto border border-border rounded-md" data-testid="mi-doctor-list">
                                    {filteredDoctors.length === 0 && (
                                        <div className="text-xs text-muted-foreground text-center py-3">No matches</div>
                                    )}
                                    {filteredDoctors.slice(0, 50).map((d) => (
                                        <button
                                            key={d.id}
                                            type="button"
                                            onClick={() => setDoctorId(d.id)}
                                            data-testid={`mi-doctor-option-${d.id}`}
                                            className={`w-full text-left px-3 py-1.5 text-sm border-b border-border/40 last:border-0 hover:bg-secondary/40 ${
                                                doctorId === d.id ? "bg-primary/10 font-semibold" : ""
                                            }`}
                                        >
                                            <span>{d.name || "—"}</span>
                                            {d.clinic_name && (
                                                <span className="text-[11px] text-muted-foreground ml-2">· {d.clinic_name}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* CASE PICKER (OPTIONAL) */}
                    {doctorId && (
                        <div>
                            <Label className="text-xs font-semibold">Link to case <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <select
                                value={caseId}
                                onChange={(e) => setCaseId(e.target.value)}
                                className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-card text-sm"
                                data-testid="mi-case-select"
                                disabled={casesLoading}
                            >
                                <option value="">— None (pure manual invoice) —</option>
                                {cases.map((c) => {
                                    const label = `${c.code || c.id.slice(0, 8)} · ${c.patient_name || "—"} · ${(c.case_type || "").replace(/_/g, " ")}`;
                                    return <option key={c.id} value={c.id}>{label}</option>;
                                })}
                            </select>
                        </div>
                    )}

                    {/* QUICK-ADD TEMPLATES */}
                    <div>
                        <Label className="text-xs font-semibold">Quick add</Label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="mi-templates">
                            {LINE_TEMPLATES.map((t) => {
                                const Icon = t.Icon;
                                return (
                                    <button
                                        key={t.key}
                                        type="button"
                                        onClick={() => addTemplate(t)}
                                        data-testid={`mi-template-${t.key}`}
                                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors"
                                    >
                                        <Icon size={12} weight="duotone" className="text-primary" />
                                        {t.label}
                                        <span className={`text-[10px] tabular-nums ${t.unit_price < 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                                            {t.unit_price < 0 ? "−" : ""}₹{Math.abs(t.unit_price).toLocaleString("en-IN")}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* LINE ITEMS */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <Label className="text-xs font-semibold">Line items <span className="text-rose-600">*</span></Label>
                            <span className="text-[10px] text-muted-foreground">Description · Qty · Unit price</span>
                        </div>
                        <div className="space-y-1.5" data-testid="mi-line-items">
                            {lineItems.map((li, idx) => (
                                <div key={li._key} className="grid grid-cols-[1fr_60px_100px_24px] gap-2 items-center">
                                    <Input
                                        value={li.description}
                                        onChange={(e) => setLi(idx, "description", e.target.value)}
                                        placeholder="What is this charge for?"
                                        className="h-8 text-sm"
                                        data-testid={`mi-line-desc-${idx}`}
                                    />
                                    <Input
                                        type="number" min="0" step="0.01"
                                        value={li.quantity}
                                        onChange={(e) => setLi(idx, "quantity", parseFloat(e.target.value) || 0)}
                                        className="h-8 text-sm"
                                        data-testid={`mi-line-qty-${idx}`}
                                    />
                                    <Input
                                        type="number" step="0.01"
                                        value={li.unit_price}
                                        onChange={(e) => setLi(idx, "unit_price", parseFloat(e.target.value) || 0)}
                                        className="h-8 text-sm"
                                        data-testid={`mi-line-price-${idx}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeLi(idx)}
                                        disabled={lineItems.length === 1}
                                        className="text-muted-foreground hover:text-rose-600 disabled:opacity-30"
                                        data-testid={`mi-line-remove-${idx}`}
                                    >
                                        <Trash size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <Button size="sm" variant="outline" onClick={addBlank} className="mt-2" data-testid="mi-add-line">
                            <Plus size={12} className="mr-1" /> Add blank line
                        </Button>
                    </div>

                    {/* META FIELDS */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Tax %</Label>
                            <Input
                                type="number" min="0" max="100" step="0.01"
                                value={taxPct}
                                onChange={(e) => setTaxPct(e.target.value)}
                                className="h-9 text-sm"
                                data-testid="mi-tax-pct"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Due date</Label>
                            <Input
                                type="date"
                                value={dueAt}
                                onChange={(e) => setDueAt(e.target.value)}
                                className="h-9 text-sm"
                                data-testid="mi-due-at"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Currency</Label>
                            <select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="mt-0.5 w-full h-9 px-2 rounded-md border border-border bg-card text-sm"
                                data-testid="mi-currency"
                            >
                                <option value="INR">INR ₹</option>
                                <option value="USD">USD $</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">Notes <span className="text-muted-foreground font-normal">(optional — visible to doctor)</span></Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="text-sm"
                            placeholder="E.g. Refund for INV-B2B-000012 aligner batch"
                            data-testid="mi-notes"
                        />
                    </div>

                    {/* LIVE TOTAL */}
                    <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-1 text-sm" data-testid="mi-totals">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="tabular-nums font-medium" data-testid="mi-total-subtotal">{formatINR(subtotal)}</span>
                        </div>
                        {taxAmount !== 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Tax ({taxPct}%)</span>
                                <span className="tabular-nums font-medium">{formatINR(taxAmount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between font-bold pt-1 border-t border-border">
                            <span>Total</span>
                            <span className="tabular-nums font-display" data-testid="mi-total-total">{formatINR(total)}</span>
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !canSubmit}
                        className="rounded-full"
                        data-testid="mi-submit"
                    >
                        <CurrencyInr size={14} className="mr-1" weight="bold" />
                        {busy ? "Creating…" : "Create invoice"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
